// client/src/session/catalog.ts — the client's mirror of the authorization vocabulary
// (03 §17.1 / 02 §6.4) · UI-003.
//
// This file is a VOCABULARY, not a policy. It lists the codes that exist so that a nav item
// or a route guard cannot name one that does not — a typo in `Fiscal.Read` would otherwise
// hide a menu entry from everybody, silently, forever. What a given user actually holds is
// never computed here: it arrives from `GET /api/v1/users/me` (02 §6.4, AUTH-004) and reaches
// the app through `useSession()`.
//
// The server side of the same list is `PermissionCatalog.cs`, which an integration test pins
// against the `app.permissions` seed (03 §17.7). Three artefacts, one list; drift on this
// side shows up as a nav item nobody can see, so `nav-items.test.ts` pins every `requires`
// value against the codes below.

/**
 * The 39 permission codes of 03 §17.1, in seed order — the 36 seeded ones, `Reports.Ledger`, added
 * by a grant migration (D-2131), and the Buildings module's two, added with its roles (D-3342).
 */
export const PERMISSION_CODES = [
  'Portfolio.Read',
  'Portfolio.Write',
  'Leasing.Read',
  'Leasing.Write',
  'Payments.Read',
  'Payments.Register',
  'Expenses.Read',
  'Expenses.Write',
  'Expenses.ManageCategories',
  'Banking.Read',
  'Banking.Import',
  'Banking.Reconcile',
  'Banking.ManageProfiles',
  'Operations.Read',
  'Operations.Write',
  'Documents.Read',
  'Documents.Write',
  'Fiscal.Read',
  'Fiscal.Evaluate',
  'Fiscal.Confirm',
  'Fiscal.Export',
  'Anaf.Read',
  'Anaf.Connect',
  'Anaf.Submit',
  'Notifications.Read',
  'Notifications.ManageRules',
  'Tasks.Manage',
  'Reports.Read',
  'Reports.Export',
  'Reports.Ledger',
  'Audit.Read',
  'Org.ManageMembers',
  'Org.Settings',
  'Portal.Self',
  'Portal.ShareLinks',
  'Platform.Admin',
  'Platform.BreakGlass',
  'Buildings.Manage',
  'Buildings.Self',
] as const;

export type Permission = (typeof PERMISSION_CODES)[number];

/**
 * The eight seeded roles — FOUNDATION_BRIEF §1.3's, `co_owner` and the Buildings module's two —
 * as `RoleCatalog.Codes` spells them.
 *
 * `co_owner` joined with D-1791. The session source refuses to build a session around a role
 * it does not know, so a co-owner who accepted a property invitation would be logged out of
 * their own account by this array alone.
 *
 * `building_admin` and `building_resident` joined with D-3341. They are held only in a clădire's
 * own organization, which `toSession` would otherwise read back as `tenant` on every row of the
 * organization list — a resident's membership mis-read without an error.
 */
export const ROLE_CODES = [
  'owner',
  'manager',
  'accountant',
  'tenant',
  'admin',
  'co_owner',
  'building_admin',
  'building_resident',
] as const;

export type RoleCode = (typeof ROLE_CODES)[number];

const KNOWN_PERMISSIONS: ReadonlySet<string> = new Set(PERMISSION_CODES);
const KNOWN_ROLES: ReadonlySet<string> = new Set(ROLE_CODES);

/** Whether a string is one of the 39 codes. Used by the nav-map test and by the session source, not by render paths. */
export function isPermission(code: string): code is Permission {
  return KNOWN_PERMISSIONS.has(code);
}

/**
 * Whether a string is one of the eight role codes. The session source refuses to build a
 * session around a role it does not know (AUTH-004): a ninth role added server-side must
 * arrive here as a deliberate change, not as a sidebar that silently shows nothing.
 */
export function isRoleCode(code: string): code is RoleCode {
  return KNOWN_ROLES.has(code);
}
